import { Form, Formik } from 'formik'
import styled from 'styled-components'

import { Box, Modal } from 'app/components/design-system-components'
import { InputField } from 'app/components/design-system-components/inputs'
import { Text } from 'app/components/design-system-components/typography'
import { CheckBoxGeneral } from 'app/components/forms/forms'
import { FieldWrapper } from 'app/forms/FieldWrapper'
import {
  formFields,
  formValidation,
} from 'app/sm/team_viewing_preference/components/members_list/EditReferrerIdModal.form.utils'
import {
  useCreateUtilityProviderReferenceId,
  useUpdateUtilityProviderReferenceId,
} from 'hooks/useUtilityProviderQueries'

type EditReferrerIdModalProps = {
  toggleModal: () => void
  providerName: string
  agencyId: string
  currentReferrerId: string
  userId: string
  allowSkip?: boolean
  onSkip?: () => void
  sendUtilityReferralToLead?: () => void
  sendUtilityReferralToLeadAfterEditingID?: boolean
}

const StyledText = styled(Text)`
  color: ${({ theme }) => theme.colors.grey600};
  line-height: 1.5;
  margin-bottom: ${({ theme }) => theme.space[5]}px;
`

const InputContainer = styled(Box)`
  margin-bottom: ${({ theme }) => theme.space[4]}px;
`

function EditReferrerIdModal({
  toggleModal,
  providerName = '',
  agencyId,
  currentReferrerId,
  userId = '',
  allowSkip = false,
  onSkip,
  sendUtilityReferralToLead,
  sendUtilityReferralToLeadAfterEditingID = true,
}: EditReferrerIdModalProps) {
  // TanStack Query mutations
  const updateReferenceIdMutation = useUpdateUtilityProviderReferenceId({
    onSuccess: () => {
      toggleModal()
      if (sendUtilityReferralToLeadAfterEditingID) {
        sendUtilityReferralToLead && sendUtilityReferralToLead()
      }
    },
  })

  const createProviderReferenceIdMutation = useCreateUtilityProviderReferenceId(
    {
      onSuccess: () => {
        toggleModal()
        if (sendUtilityReferralToLeadAfterEditingID) {
          sendUtilityReferralToLead && sendUtilityReferralToLead()
        }
      },
    },
  )

  const initialValues = {
    [formFields.referrerId]: currentReferrerId,
    [formFields.applyToAllTeamMembers]: false,
  }

  const onSubmit = (values: typeof initialValues) => {
    const referrerId = (values[formFields.referrerId] as string)?.trim()
    const applyToAllTeamMembers = values[
      formFields.applyToAllTeamMembers
    ] as boolean

    if (applyToAllTeamMembers) {
      // Confirm with user before applying to all team members
      const confirmed = window.confirm(
        'Are you sure you want to set this referrer ID for all team members? This will override any existing referrer IDs.',
      )

      if (!confirmed) {
        return
      }

      // Apply to all team members using POST endpoint
      createProviderReferenceIdMutation.mutate({
        agencyId,
        referenceId: referrerId || '',
      } as any)
    } else {
      // Apply to individual user using PATCH endpoint
      updateReferenceIdMutation.mutate({
        agencyId,
        referenceId: referrerId || '',
        userId: userId,
      } as any)
    }
  }

  // Get loading state from mutations
  const isLoading =
    updateReferenceIdMutation.isPending ||
    createProviderReferenceIdMutation.isPending

  // Handle skip action
  const handleSkip = () => {
    if (sendUtilityReferralToLeadAfterEditingID) {
      sendUtilityReferralToLead && sendUtilityReferralToLead()
    }
    if (onSkip) {
      onSkip()
    }
    toggleModal()
  }

  // Determine secondary button configuration
  const secondaryLabel = allowSkip ? 'Send Referral' : 'Cancel'
  const secondaryAction = allowSkip ? handleSkip : toggleModal
  const primaryLabel = sendUtilityReferralToLeadAfterEditingID
    ? 'Save & Send'
    : 'Save'

  const modalText = allowSkip
    ? `You don't have a Utility Connection Service (UCS) Referrer ID set. Set the Utility Connection Service (UCS) Referrer ID for ${providerName} to correctly attribute referrals.`
    : `Set the Utility Connection Service (UCS) Referrer ID for ${providerName} to correctly attribute referrals.`

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validate={formValidation}
      validateOnMount
      validateOnBlur
      validateOnChange
    >
      {({ values, setFieldValue, submitForm }) => {
        const modalBody = (
          <Form>
            <Box>
              <StyledText>{modalText}</StyledText>

              <InputContainer>
                <FieldWrapper
                  id={formFields.referrerId}
                  name={formFields.referrerId}
                  label={`UCS Referrer ID`}
                >
                  <InputField
                    name={formFields.referrerId}
                    inputProps={{
                      placeholder: 'e.g. referrer123 (optional)',
                    }}
                  />
                </FieldWrapper>
              </InputContainer>

              <CheckBoxGeneral
                id={formFields.applyToAllTeamMembers}
                label="Set all team members to the same UCS Referrer ID"
                checked={values[formFields.applyToAllTeamMembers]}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setFieldValue(
                    formFields.applyToAllTeamMembers,
                    event.target.checked,
                  )
                }}
                eventProcessedByComponent
              />
            </Box>
          </Form>
        )

        return (
          <Modal
            modalHeading="Edit UCS referrer ID"
            modalBody={modalBody}
            toggleModal={toggleModal}
            primaryLabel={primaryLabel}
            secondaryLabel={secondaryLabel}
            primaryAction={() => {
              // Use Formik's submitForm to avoid submitting other forms on the page
              submitForm()
            }}
            secondaryAction={secondaryAction}
            primaryButtonDisabled={isLoading}
            primaryButtonLoading={isLoading}
            showSecondaryButton={true}
            modalFooter={undefined}
          />
        )
      }}
    </Formik>
  )
}

export default EditReferrerIdModal
